import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { HomeRedirect } from "@/components/auth/HomeRedirect";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { ReviewRoute } from "@/components/auth/ReviewRoute";
import { useApplySettings } from "@/hooks/useApplySettings";
import { useSessionTimeout } from "@/hooks/useSessionTimeout";
import { SessionTimeoutWarning } from "@/components/SessionTimeoutWarning";
import { UpdatePrompt } from "@/components/UpdatePrompt";
import { SplashScreen } from "@/components/SplashScreen";
import { NativeRuntime } from "@/components/native/NativeRuntime";

// Lazy with auto-reload on stale chunk (after a new deploy, old hashed chunks 404)
const RELOAD_KEY = "tk:chunk-reload";
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function lazyWithRetry<T extends { default: React.ComponentType<any> }>(
  factory: () => Promise<T>
) {
  return lazy(async () => {
    try {
      return await factory();
    } catch (err: unknown) {
      const msg = String((err as Error)?.message || err);
      const isChunkError =
        msg.includes("Failed to fetch dynamically imported module") ||
        msg.includes("Importing a module script failed") ||
        msg.includes("error loading dynamically imported module");
      if (isChunkError && typeof window !== "undefined") {
        const already = sessionStorage.getItem(RELOAD_KEY);
        if (!already) {
          sessionStorage.setItem(RELOAD_KEY, "1");
          window.location.reload();
          return new Promise<T>(() => {}); // never resolves; reload takes over
        }
      }
      throw err;
    }
  });
}

// Clear the guard once the app successfully mounts a new bundle
if (typeof window !== "undefined") {
  setTimeout(() => sessionStorage.removeItem(RELOAD_KEY), 5000);
}

// Lazy-loaded pages
const Home = lazyWithRetry(() => import("./pages/Home"));
const Privacidade = lazyWithRetry(() => import("./pages/LegalPages").then((m) => ({ default: m.Privacidade })));
const Termos = lazyWithRetry(() => import("./pages/LegalPages").then((m) => ({ default: m.Termos })));
const Suporte = lazyWithRetry(() => import("./pages/LegalPages").then((m) => ({ default: m.Suporte })));
const ExcluirConta = lazyWithRetry(() => import("./pages/LegalPages").then((m) => ({ default: m.ExcluirConta })));
const Index = lazyWithRetry(() => import("./pages/Index"));
const PrincipalHub = lazyWithRetry(() => import("./pages/PrincipalHub"));
const GestaoHub = lazyWithRetry(() => import("./pages/GestaoHub"));
const Auth = lazyWithRetry(() => import("./pages/Auth"));
const ReviewMode = lazyWithRetry(() => import("./pages/ReviewMode"));
const DefinirSenha = lazyWithRetry(() => import("./pages/DefinirSenha"));
const Agenda = lazyWithRetry(() => import("./pages/Agenda"));
const Casos = lazyWithRetry(() => import("./pages/Casos"));
const Clube = lazyWithRetry(() => import("./pages/Clube"));
const ClubeAdmin = lazyWithRetry(() => import("./pages/ClubeAdmin"));
const Clientes = lazyWithRetry(() => import("./pages/Clientes"));
const ClientesDuplicados = lazyWithRetry(() => import("./pages/ClientesDuplicados"));
const Procedimentos = lazyWithRetry(() => import("./pages/Procedimentos"));
const Estoque = lazyWithRetry(() => import("./pages/Estoque"));
const Fornecedores = lazyWithRetry(() => import("./pages/Fornecedores"));
const EntradaEstoque = lazyWithRetry(() => import("./pages/EntradaEstoque"));
const NFeInbox = lazyWithRetry(() => import("./pages/NFeInbox"));
const FinanceiroHub = lazyWithRetry(() => import("./pages/financeiro/FinanceiroHub"));
const FinanceiroDashboard = lazyWithRetry(() => import("./pages/financeiro/FinanceiroDashboard"));
const FinanceiroTransacoes = lazyWithRetry(() => import("./pages/financeiro/FinanceiroTransacoes"));
const FinanceiroReceber = lazyWithRetry(() => import("./pages/financeiro/FinanceiroReceber"));
const FinanceiroPagar = lazyWithRetry(() => import("./pages/financeiro/FinanceiroPagar"));
const FinanceiroExtratos = lazyWithRetry(() => import("./pages/financeiro/FinanceiroExtratos"));
const FinanceiroConciliacao = lazyWithRetry(() => import("./pages/financeiro/FinanceiroConciliacao"));
const FinanceiroFechamento = lazyWithRetry(() => import("./pages/financeiro/FinanceiroFechamento"));
const FinanceiroRelatorios = lazyWithRetry(() => import("./pages/financeiro/FinanceiroRelatorios"));
const FinanceiroCaixa = lazyWithRetry(() => import("./pages/financeiro/FinanceiroCaixa"));
const FinanceiroCashback = lazyWithRetry(() => import("./pages/financeiro/FinanceiroCashback"));
const Compras = lazyWithRetry(() => import("./pages/Compras"));
const AdminHub = lazyWithRetry(() => import("./pages/admin/AdminHub"));
const AdminUsuarios = lazyWithRetry(() => import("./pages/admin/AdminUsuarios"));
const AdminPerfis = lazyWithRetry(() => import("./pages/admin/AdminPerfis"));
const AdminTiposAgendamento = lazyWithRetry(() => import("./pages/admin/AdminTiposAgendamento"));
const AdminPagamentos = lazyWithRetry(() => import("./pages/admin/AdminPagamentos"));
const AdminCategoriasFinanceiras = lazyWithRetry(() => import("./pages/admin/AdminCategoriasFinanceiras"));
const ImportacaoBelle = lazyWithRetry(() => import("./pages/ImportacaoBelle"));
const AdminBelleIntegration = lazyWithRetry(() => import("./pages/admin/AdminBelleIntegration"));
const AdminContasBancarias = lazyWithRetry(() => import("./pages/admin/AdminContasBancarias"));
const AdminResumoDiario = lazyWithRetry(() => import("./pages/admin/AdminResumoDiario"));
const AdminAgendaProfissionais = lazyWithRetry(() => import("./pages/admin/AdminAgendaProfissionais"));
const AdminLandingPage = lazyWithRetry(() => import("./pages/admin/AdminLandingPage"));
const AdminIntegracaoComercial = lazyWithRetry(() => import("./pages/admin/AdminIntegracaoComercial"));
const AdminPrescricoes = lazyWithRetry(() => import("./pages/admin/AdminPrescricoes"));
const Perfil = lazyWithRetry(() => import("./pages/Perfil"));
const UserDetails = lazyWithRetry(() => import("./pages/UserDetails"));
const Configuracoes = lazyWithRetry(() => import("./pages/Configuracoes"));
const Notificacoes = lazyWithRetry(() => import("./pages/Notificacoes"));
const ImportarClientes = lazyWithRetry(() => import("./pages/ImportarClientes"));
const AnamnesePublica = lazyWithRetry(() => import("./pages/AnamnesePublica"));
const OrcamentoPublico = lazyWithRetry(() => import("./pages/OrcamentoPublico"));
const CadastroToken = lazyWithRetry(() => import("./pages/CadastroToken"));
const ClienteLogin = lazyWithRetry(() => import("./pages/ClienteLogin"));
const ClientePortal = lazyWithRetry(() => import("./pages/ClientePortal"));
const MarketingHub = lazyWithRetry(() => import("./pages/marketing/MarketingHub"));
const MarketingCampanhas = lazyWithRetry(() => import("./pages/marketing/MarketingCampanhas"));
const MarketingLeads = lazyWithRetry(() => import("./pages/marketing/MarketingLeads"));
const MarketingPerformance = lazyWithRetry(() => import("./pages/marketing/MarketingPerformance"));
const MarketingInstagram = lazyWithRetry(() => import("./pages/marketing/MarketingInstagram"));
const MarketingWhatsApp = lazyWithRetry(() => import("./pages/marketing/MarketingWhatsApp"));
const MarketingBusinessAds = lazyWithRetry(() => import("./pages/marketing/MarketingBusinessAds"));
const Prontuario = lazyWithRetry(() => import("./pages/Prontuario"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));

function LazyFallback() {
  return <SplashScreen />;
}


// Cashback é invalidado só pelas operações relacionadas, via invalidateCashbackDependents (src/lib/cashbackInvalidation.ts).
const queryClient: QueryClient = new QueryClient();

function AppContent() {
  // Apply appearance settings (primary color)
  useApplySettings();
  
  // Session timeout management
  const { showWarning, remainingTime, extendSession, logout } = useSessionTimeout();

  return (
    <>
      <UpdatePrompt />
      <NativeRuntime />
      <SessionTimeoutWarning
        open={showWarning}
        remainingTime={remainingTime}
        onExtend={extendSession}
        onLogout={logout}
      />
      <Suspense fallback={<LazyFallback />}>
        <Routes>
          <Route path="/auth" element={<Auth />} />
          <Route path="/revisao" element={<ReviewRoute><ReviewMode /></ReviewRoute>} />
          <Route path="/definir-senha" element={<DefinirSenha />} />
          <Route path="/privacidade" element={<Privacidade />} />
          <Route path="/termos" element={<Termos />} />
          <Route path="/suporte" element={<Suporte />} />
          <Route path="/excluir-conta" element={<ExcluirConta />} />
          <Route path="/anamnese/:clientId" element={<AnamnesePublica />} />
          <Route path="/orcamento/:token" element={<OrcamentoPublico />} />
          <Route path="/cadastro/:token" element={<CadastroToken />} />
          <Route path="/cliente" element={<ClienteLogin />} />
          <Route path="/cliente/portal/:token" element={<ClientePortal />} />
          <Route path="/" element={<HomeRedirect home={<Home />} />} />
          <Route path="/dashboard" element={
            <ProtectedRoute requiredModule="dashboard">
              <Index />
            </ProtectedRoute>
          } />
          <Route path="/principal" element={
            <ProtectedRoute requiredModule="dashboard">
              <PrincipalHub />
            </ProtectedRoute>
          } />
          <Route path="/gestao" element={
            <ProtectedRoute requiredModule="dashboard">
              <GestaoHub />
            </ProtectedRoute>
          } />
          <Route path="/casos" element={
            <ProtectedRoute requiredModule="cases">
              <Casos />
            </ProtectedRoute>
          } />
          <Route path="/clube" element={
            <ProtectedRoute requiredModule="club">
              <Clube />
            </ProtectedRoute>
          } />
          <Route path="/clube/admin" element={
            <ProtectedRoute requiredModule="club">
              <ClubeAdmin />
            </ProtectedRoute>
          } />
          <Route path="/agenda" element={
            <ProtectedRoute requiredModule="agenda">
              <Agenda />
            </ProtectedRoute>
          } />
          <Route path="/clientes" element={
            <ProtectedRoute requiredModule="clients">
              <Clientes />
            </ProtectedRoute>
          } />
          <Route path="/clientes/duplicados" element={
            <ProtectedRoute requiredRole="admin">
              <ClientesDuplicados />
            </ProtectedRoute>
          } />
          <Route path="/procedimentos" element={
            <ProtectedRoute requiredModule="procedures">
              <Procedimentos />
            </ProtectedRoute>
          } />
          <Route path="/estoque" element={
            <ProtectedRoute requiredModule="inventory">
              <Estoque />
            </ProtectedRoute>
          } />
          <Route path="/fornecedores" element={
            <ProtectedRoute requiredModule="suppliers">
              <Fornecedores />
            </ProtectedRoute>
          } />
          <Route path="/estoque/entrada" element={
            <ProtectedRoute requiredModule="inventory">
              <EntradaEstoque />
            </ProtectedRoute>
          } />
          <Route path="/estoque/nfe" element={
            <ProtectedRoute requiredModule="nfe">
              <NFeInbox />
            </ProtectedRoute>
          } />
          <Route path="/financeiro" element={
            <ProtectedRoute requiredModule="financial">
              <FinanceiroHub />
            </ProtectedRoute>
          } />
          <Route path="/financeiro/dashboard" element={
            <ProtectedRoute requiredModule="financial">
              <FinanceiroDashboard />
            </ProtectedRoute>
          } />
          <Route path="/financeiro/transacoes" element={
            <ProtectedRoute requiredModule="financial">
              <FinanceiroTransacoes />
            </ProtectedRoute>
          } />
          <Route path="/financeiro/receber" element={
            <ProtectedRoute requiredModule="financial">
              <FinanceiroReceber />
            </ProtectedRoute>
          } />
          <Route path="/financeiro/pagar" element={
            <ProtectedRoute requiredModule="financial">
              <FinanceiroPagar />
            </ProtectedRoute>
          } />
          <Route path="/financeiro/extratos" element={
            <ProtectedRoute requiredModule="financial">
              <FinanceiroExtratos />
            </ProtectedRoute>
          } />
          <Route path="/financeiro/conciliacao" element={
            <ProtectedRoute requiredModule="financial">
              <FinanceiroConciliacao />
            </ProtectedRoute>
          } />
          <Route path="/financeiro/caixa" element={
            <ProtectedRoute>
              <FinanceiroCaixa />
            </ProtectedRoute>
          } />
          <Route path="/financeiro/cashback" element={
            <ProtectedRoute requiredModule="financial">
              <FinanceiroCashback />
            </ProtectedRoute>
          } />
          <Route path="/financeiro/fechamento" element={
            <ProtectedRoute requiredModule="reports">
              <FinanceiroFechamento />
            </ProtectedRoute>
          } />
          <Route path="/financeiro/relatorios" element={
            <ProtectedRoute requiredModule="reports">
              <FinanceiroRelatorios />
            </ProtectedRoute>
          } />
          <Route path="/compras" element={
            <ProtectedRoute requiredModule="purchases">
              <Compras />
            </ProtectedRoute>
          } />
          <Route path="/admin" element={
            <ProtectedRoute requiredModule="admin">
              <AdminHub />
            </ProtectedRoute>
          } />
          <Route path="/admin/usuarios" element={
            <ProtectedRoute requiredModule="admin">
              <AdminUsuarios />
            </ProtectedRoute>
          } />
          <Route path="/admin/perfis" element={
            <ProtectedRoute requiredModule="admin">
              <AdminPerfis />
            </ProtectedRoute>
          } />
          <Route path="/admin/tipos-agendamento" element={
            <ProtectedRoute requiredModule="admin">
              <AdminTiposAgendamento />
            </ProtectedRoute>
          } />
          <Route path="/admin/pagamentos" element={
            <ProtectedRoute requiredModule="admin">
              <AdminPagamentos />
            </ProtectedRoute>
          } />
          <Route path="/admin/categorias-financeiras" element={
            <ProtectedRoute requiredModule="admin">
              <AdminCategoriasFinanceiras />
            </ProtectedRoute>
          } />
          <Route path="/importacao-belle" element={
            <ProtectedRoute requiredModule="clients">
              <ImportacaoBelle />
            </ProtectedRoute>
          } />
          <Route path="/admin/resumo-diario" element={
            <ProtectedRoute requiredModule="admin">
              <AdminResumoDiario />
            </ProtectedRoute>
          } />
          <Route path="/admin/belle" element={
            <ProtectedRoute requiredModule="admin">
              <AdminBelleIntegration />
            </ProtectedRoute>
          } />
          <Route path="/admin/integracao-comercial" element={
            <ProtectedRoute requiredModule="commercial_integration">
              <AdminIntegracaoComercial />
            </ProtectedRoute>
          } />
          <Route path="/admin/prescricoes" element={
            <ProtectedRoute requiredModule="admin">
              <AdminPrescricoes />
            </ProtectedRoute>
          } />
          <Route path="/admin/contas-bancarias" element={
            <ProtectedRoute requiredModule="admin">
              <AdminContasBancarias />
            </ProtectedRoute>
          } />
          <Route path="/admin/pagina-principal" element={
            <ProtectedRoute requiredModule="admin">
              <AdminLandingPage />
            </ProtectedRoute>
          } />
          <Route path="/agenda-profissionais" element={
            <ProtectedRoute requiredModule="professional_schedule">
              <AdminAgendaProfissionais />
            </ProtectedRoute>
          } />
          <Route path="/admin/usuario/:id" element={
            <ProtectedRoute requiredModule="admin">
              <UserDetails />
            </ProtectedRoute>
          } />
          <Route path="/perfil" element={
            <ProtectedRoute>
              <Perfil />
            </ProtectedRoute>
          } />
          <Route path="/configuracoes" element={
            <ProtectedRoute requiredModule="settings">
              <Configuracoes />
            </ProtectedRoute>
          } />
          <Route path="/notificacoes" element={
            <ProtectedRoute>
              <Notificacoes />
            </ProtectedRoute>
          } />
          <Route path="/importar-clientes" element={
            <ProtectedRoute requiredModule="clients">
              <ImportarClientes />
            </ProtectedRoute>
          } />
          <Route path="/marketing" element={
            <ProtectedRoute requiredModule="marketing">
              <MarketingHub />
            </ProtectedRoute>
          } />
          <Route path="/marketing/campanhas" element={
            <ProtectedRoute requiredModule="marketing">
              <MarketingCampanhas />
            </ProtectedRoute>
          } />
          <Route path="/marketing/leads" element={
            <ProtectedRoute requiredModule="marketing">
              <MarketingLeads />
            </ProtectedRoute>
          } />
          <Route path="/marketing/performance" element={
            <ProtectedRoute requiredModule="marketing">
              <MarketingPerformance />
            </ProtectedRoute>
          } />
          <Route path="/marketing/instagram" element={
            <ProtectedRoute requiredModule="marketing">
              <MarketingInstagram />
            </ProtectedRoute>
          } />
          <Route path="/marketing/instagram/:tab" element={
            <ProtectedRoute requiredModule="marketing">
              <MarketingInstagram />
            </ProtectedRoute>
          } />
          <Route path="/marketing/whatsapp" element={
            <ProtectedRoute requiredModule="marketing">
              <MarketingWhatsApp />
            </ProtectedRoute>
          } />
          <Route path="/marketing/whatsapp/:tab" element={
            <ProtectedRoute requiredModule="marketing">
              <MarketingWhatsApp />
            </ProtectedRoute>
          } />
          <Route path="/marketing/business-ads" element={
            <ProtectedRoute requiredModule="marketing">
              <MarketingBusinessAds />
            </ProtectedRoute>
          } />
          <Route path="/marketing/business-ads/:tab" element={
            <ProtectedRoute requiredModule="marketing">
              <MarketingBusinessAds />
            </ProtectedRoute>
          } />
          <Route path="/prontuario/:appointmentId" element={
            <ProtectedRoute requiredModule="medical_records">
              <Prontuario />
            </ProtectedRoute>
          } />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </>
  );
}

/** `Router` só é trocado pela entrada isolada da demonstração (HashRouter); o app real usa BrowserRouter. */
const App = ({ Router = BrowserRouter }: { Router?: React.ComponentType<{ children?: React.ReactNode }> }) => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider delayDuration={0}>
      <Toaster />
      <Sonner />
      <Router>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </Router>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
